import { createContext, useContext, useState } from 'react';
import { useQueries, useQueryClient } from '@zg-rentals/react-query5';
import type { RetrieveActiveDocumentsWebApiResponseDto } from '@zg-rentals/ts-capps-client';
import { CappsRenterDocumentsWebApiHandler_retrieveActiveDocuments_queryOptions } from '@zg-rentals/ts-capps-client-react-query';
import type { Document } from '../models/rental-application';
import { isRedactionCheckComplete, toDocument } from './useRetrieveActiveDocuments';

const MAX_DOCUMENT_POLLS = 10;
// Less than 1 second is forbidden by the API spec
export const DOCUMENT_POLL_INTERVAL_MS = 6000;

export type DocumentDetailsStatus = 'loading' | 'ready' | 'inactive' | 'error';

function getOptions(documentId: string) {
  return CappsRenterDocumentsWebApiHandler_retrieveActiveDocuments_queryOptions({ documentIds: [documentId] });
}

function getDocumentIds(documents: Array<Document>) {
  return [...new Set(documents.flatMap(({ id }) => (id ? [id] : [])))];
}

function findDetail(response: RetrieveActiveDocumentsWebApiResponseDto | undefined, documentId: string) {
  return response?.documentInfoList.find((document) => document.documentId === documentId);
}

// capps returns only unexpired files, so an answer without the file also ends its polling
function isDone(response: RetrieveActiveDocumentsWebApiResponseDto | undefined, documentId: string) {
  if (!response) {
    return false;
  }
  const detail = findDetail(response, documentId);
  return !detail || isRedactionCheckComplete(detail);
}

/**
 * Polls retrieveActiveDocuments with one query per file, so a finished file is not fetched again when another one
 * is added or removed. Each file stops after 10 polls, or after 3 failed requests in a row
 */
export function useStepDocumentDetails(documents: Array<Document>) {
  const queryClient = useQueryClient();
  const ids = getDocumentIds(documents);
  const [areDocumentsExpired, setAreDocumentsExpired] = useState(false);

  const results = useQueries({
    queries: ids.map((documentId) => ({
      ...getOptions(documentId),
      // 2 retries mean 3 failures in a row before the query is in error
      retry: 2,
      retryDelay: DOCUMENT_POLL_INTERVAL_MS,
      // A file that stopped on errors tries again when the step mounts again; a finished file does not
      refetchOnMount: ({ state }: { state: { status: string } }) => state.status === 'error',
      refetchInterval: ({
        state,
      }: {
        state: { status: string; data?: RetrieveActiveDocumentsWebApiResponseDto; dataUpdateCount: number };
      }) =>
        state.status === 'error' || isDone(state.data, documentId) || state.dataUpdateCount >= MAX_DOCUMENT_POLLS
          ? false
          : DOCUMENT_POLL_INTERVAL_MS,
      meta: {
        tags: ['incomeDocuments'],
        context: { api: 'cappsRenterDocuments', details: 'retrieveActiveDocuments' },
      },
    })),
  });
  const resultOf = (documentId: string) => results[ids.indexOf(documentId)];

  // capps sets this flag once per file, and the file's query can go away later, so the step keeps it
  if (!areDocumentsExpired && results.some(({ data }) => data?.areDocumentsExpired)) {
    setAreDocumentsExpired(true);
  }

  const detailOf = (documentId: string) => {
    const detail = findDetail(resultOf(documentId)?.data, documentId);
    return detail && toDocument(detail);
  };

  const statusOf = (documentId: string): DocumentDetailsStatus => {
    const result = resultOf(documentId);
    // A file that is not polled came with its details
    if (!result) {
      return 'ready';
    }
    if (result.data) {
      return findDetail(result.data, documentId) ? 'ready' : 'inactive';
    }
    return result.status === 'error' ? 'error' : 'loading';
  };

  /** True for a file that is not done when its polling stopped */
  const hasPollEnded = (documentId: string) => {
    const result = resultOf(documentId);
    // fetchStatus also makes the step render again when the last poll ends with the same data
    return (
      !!result &&
      result.fetchStatus === 'idle' &&
      !isDone(result.data, documentId) &&
      (result.status === 'error' ||
        (queryClient.getQueryState(getOptions(documentId).queryKey)?.dataUpdateCount ?? 0) >= MAX_DOCUMENT_POLLS)
    );
  };

  return {
    detailOf,
    statusOf,
    hasPollEnded,
    isError: results.some(({ status, data }) => status === 'error' && !data),
    areDocumentsExpired,
  };
}

export type StepDocumentDetails = ReturnType<typeof useStepDocumentDetails>;

export const StepDocumentDetailsContext = createContext<StepDocumentDetails | undefined>(undefined);

export function useStepDocumentDetailsContext() {
  const details = useContext(StepDocumentDetailsContext);
  if (!details) {
    throw new Error('useStepDocumentDetailsContext needs a StepDocumentDetailsContext provider');
  }
  return details;
}
